import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api, type ApiError, type ListRecipesResult } from '@whinerp/client';
import { Button, ErrorState, PageHeading, Panel, Skeleton } from '@whinerp/ui';
import { useErrorMessage, useT } from '../../app/i18n.js';
import { useSession } from '../../app/session.js';
import { defineScreen } from '../types.js';

/**
 * SC-70 Automation templates (F-094): a gallery with category filters and a
 * plain-English description of each recipe. Installing opens the builder with
 * the recipe as a draft; it is never switched on by installing it.
 */

type Category = ListRecipesResult['recipes'][number]['category'];
const CATEGORIES: Category[] = ['routing', 'hygiene', 'nurture', 'follow-up', 'alerting'];

function RecipeGalleryScreen(): JSX.Element {
  const t = useT();
  const errorText = useErrorMessage();
  const navigate = useNavigate();
  const { can } = useSession();
  const [category, setCategory] = useState<Category | 'all'>('all');
  const recipes = useQuery<ListRecipesResult, ApiError>({ queryKey: ['recipes'], queryFn: () => api.listRecipes({}) });
  const install = useMutation({
    mutationFn: (key: string) => api.installRecipe({ path: { key }, body: {} }),
    onSuccess: (installed) => navigate(installed.href),
  });
  if (recipes.isPending) return <Skeleton className="h-plot w-full" />;
  if (recipes.isError) return <ErrorState title={t('state.error')} body={errorText(recipes.error)} onRetry={() => void recipes.refetch()} />;
  const shown = recipes.data.recipes.filter((recipe) => category === 'all' || recipe.category === category);
  return (
    <div data-component="RecipeGallery" className="flex w-full flex-col gap-4">
      <PageHeading>{t('recipes.title')}</PageHeading>
      <p className="text-body text-ink-meta">{t('recipes.help')}</p>
      <div role="group" aria-label={t('recipes.category')} className="flex flex-wrap gap-2">
        {(['all', ...CATEGORIES] as const).map((option) => (
          <Button key={option} intent={category === option ? 'primary' : 'secondary'} size="small" data-recipe-category={option} onClick={() => setCategory(option)}>
            {t(`recipes.category.${option}`)}
          </Button>
        ))}
      </div>
      {install.error ? <p className="text-dense text-danger-text">{errorText(install.error)}</p> : null}
      <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {shown.map((recipe) => (
          <li key={recipe.key}>
            <Panel data-recipe={recipe.key}>
              <div className="flex flex-col gap-2">
                <span className="text-meta text-ink-meta">
                  {t(`recipes.category.${recipe.category}`)} · {t(`recipes.palette.${recipe.palette}`)}
                </span>
                <span className="text-body text-ink">{recipe.name}</span>
                <p className="text-dense text-ink-600">{recipe.description}</p>
                <ol className="list-decimal ps-5 text-meta text-ink-meta">
                  {recipe.steps.map((step, index) => (
                    <li key={`${recipe.key}-${String(index)}`}>{step}</li>
                  ))}
                </ol>
                {can('automation.manage') ? (
                  <div>
                    <Button intent="secondary" size="small" data-recipe-install disabled={install.isPending} onClick={() => install.mutate(recipe.key)}>
                      {t('recipes.install')}
                    </Button>
                  </div>
                ) : null}
              </div>
            </Panel>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Loading(): JSX.Element {
  return <Skeleton className="h-plot w-full" />;
}
function Failed(): JSX.Element {
  const t = useT();
  return <ErrorState title={t('state.error')} body={t('recipes.help')} />;
}

export const screen = defineScreen({
  id: 'SC-70',
  route: '/automations/templates',
  surface: 'page',
  title: 'Automation templates',
  features: ['F-094'],
  entitlement: 'F-094',
  reachedBy: [{ kind: 'screen', from: 'SC-52', how: 'Browse templates on the automations list' }],
  components: [],
  elements: ['RecipeGallery'],
  states: { empty: Loading, loading: Loading, error: Failed },
  component: RecipeGalleryScreen,
});

export default screen;
